import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createRequirement } from '../../store/slices/staffingSlice';
import Layout from '../../components/Layout';
import api from '../../services/api';
import { STAFFING_REQUIREMENT_STATUS } from '../../utils/constants';

const StaffingRequirementForm = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [staffingEmployees, setStaffingEmployees] = useState([]);

  const [formData, setFormData] = useState({
    assigned_to: [],
    client_id: clientId,
    title: '',
    skills_required: '',
    experience_required: '',
    number_of_positions: 1,
    client_budget: '',
    status: STAFFING_REQUIREMENT_STATUS.INITIATED
  });

  useEffect(() => {
    // Fetch staffing sales employees for assignment
    const fetchStaffingEmployees = async () => {
      try {
        const response = await api.get('/employees/by-department?department=staffing_sales');
        setStaffingEmployees(response.data.data);
      } catch (err) {
        console.error('Failed to fetch staffing employees:', err);
      }
    };
    fetchStaffingEmployees();
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    if (name === 'assigned_to') {
      // Handle multi-select for assigned employees
      const options = Array.from(e.target.selectedOptions, option => option.value);
      setFormData({
        ...formData,
        assigned_to: options
      });
    } else {
      const val = type === 'number' ? parseInt(value) : value;
      setFormData({
        ...formData,
        [name]: val
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Convert comma-separated skills to array
      const submitData = {
        ...formData,
        skills_required: formData.skills_required.split(',').map(s => s.trim()).filter(s => s)
      };

      await dispatch(createRequirement(submitData)).unwrap();
      navigate(`/staffing/clients/${clientId}`);
    } catch (err) {
      setError(err.message || err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container-fluid" style={{ maxWidth: '800px' }}>
        <button className="btn btn-outline-secondary mb-3" onClick={() => navigate(`/staffing/clients/${clientId}`)}>
          <i className="ti ti-arrow-left me-1" />
          Back to Client
        </button>

        <div className="card">
          <div className="card-body">
            <h2 className="fs-5 mb-3">Create New Requirement</h2>

            {error && <div className="alert alert-danger">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Assigned To (Hold Ctrl/Cmd to select multiple)</label>
                <select
                  name="assigned_to"
                  className="form-select"
                  value={formData.assigned_to}
                  onChange={handleChange}
                  multiple
                  style={{ minHeight: '100px' }}
                >
                  {staffingEmployees.map((emp) => (
                    <option key={emp._id} value={emp._id}>
                      {emp.name} ({emp.employee_code})
                    </option>
                  ))}
                </select>
                <small className="text-muted d-block mt-1">
                  Selected: {formData.assigned_to.length > 0
                    ? staffingEmployees.filter(emp => formData.assigned_to.includes(emp._id)).map(emp => emp.name).join(', ')
                    : 'None (Optional)'}
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label">Position Title *</label>
                <input
                  type="text"
                  name="title"
                  className="form-control"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., Full Stack Developer"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Skills Required *</label>
                <input
                  type="text"
                  name="skills_required"
                  className="form-control"
                  value={formData.skills_required}
                  onChange={handleChange}
                  placeholder="e.g., React, Node.js, MongoDB (comma-separated)"
                  required
                />
                <small className="text-muted d-block mt-1">
                  Enter skills separated by commas
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label">Experience Required *</label>
                <input
                  type="text"
                  name="experience_required"
                  className="form-control"
                  value={formData.experience_required}
                  onChange={handleChange}
                  placeholder="e.g., 3-5 years"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Client Budget</label>
                <input
                  type="text"
                  name="client_budget"
                  className="form-control"
                  value={formData.client_budget}
                  onChange={handleChange}
                  placeholder="e.g., $50,000 - $70,000 per annum"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Number of Positions *</label>
                <input
                  type="number"
                  name="number_of_positions"
                  className="form-control"
                  value={formData.number_of_positions}
                  onChange={handleChange}
                  min="1"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Status *</label>
                <select
                  name="status"
                  className="form-select"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  {Object.values(STAFFING_REQUIREMENT_STATUS).map((status) => (
                    <option key={status} value={status}>
                      {status.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div className="d-flex gap-2 justify-content-end mt-4">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(`/staffing/clients/${clientId}`)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Requirement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StaffingRequirementForm;
